/**
 * kc-sofa-edit.js
 * "Edit sofa" round trip — cart → configurator → cart — WITHOUT duplicating the sofa.
 *
 * The constraint: the configurator bridge (assets/product-configurator.js, a build artefact of the
 * livom-configurator repo — not edited here) only knows how to ADD. Every add mints a fresh `_set_id`,
 * so re-adding an edited sofa would leave the old one in the cart next to it.
 *
 * What the theme does about it, in three steps:
 *
 *   1. The "Edit" link of a sofa (snippets/kc-cart-sofa.liquid) is its `_link` plus `?edit_set=<its _set_id>`.
 *   2. On the configurator page, that param opens an edit session: we record which sofa is being edited and
 *      which sets are in the cart right now. No param = a normal visit, and any stale session is dropped.
 *   3. Back on /cart (the bridge always redirects there), a set that was NOT in that snapshot is the edited
 *      sofa. Only then is the old set removed — so exactly the sofa that was opened gets replaced, never
 *      another one, and a second sofa in the cart is never touched.
 *
 * It never deletes before the new sofa is safely in the cart: if the add fails, or the customer leaves the
 * configurator without saving, the original is still there and the session simply expires.
 *
 * `edit_set` is also forwarded to the configurator iframe (the section passes the whole query string on), so
 * the configurator can switch its CTA to "Save changes" when it sees it. Until it does, the notice below tells
 * the customer that adding replaces the sofa they opened.
 */
(() => {
  const KEY = "kc:sofa-edit";
  const DONE_KEY = "kc:sofa-edit-done";
  const TTL = 6 * 60 * 60 * 1000;
  const RECONCILING_CLASS = "kc-cart-reconciling";
  const script = document.currentScript;

  const root = () => (window.Shopify && Shopify.routes && Shopify.routes.root) || "/";

  function read() {
    try {
      const record = JSON.parse(localStorage.getItem(KEY));
      if (record && record.old && Array.isArray(record.known) && Date.now() - record.ts < TTL) return record;
    } catch (error) {
      /* storage blocked or malformed: treat as "no session" */
    }
    return null;
  }

  function write(record) {
    try {
      localStorage.setItem(KEY, JSON.stringify(record));
    } catch (error) {
      /* private mode: editing still works, it just adds instead of replacing */
    }
  }

  function clear() {
    try {
      localStorage.removeItem(KEY);
    } catch (error) {
      /* nothing to clear */
    }
  }

  async function fetchCart() {
    const response = await fetch(`${root()}cart.js`, { headers: { Accept: "application/json" } });
    if (!response.ok) throw new Error(`cart.js ${response.status}`);
    return response.json();
  }

  function setIdsOf(cart) {
    const ids = new Set();
    (cart.items || []).forEach((item) => {
      const properties = item.properties || {};
      const id = properties._set_id || properties._set;
      if (id) ids.add(id);
    });
    return [...ids];
  }

  /* ---------------------------------------------------------------- *
   * Configurator page
   * ---------------------------------------------------------------- */

  async function onConfiguratorPage() {
    const editSet = new URLSearchParams(window.location.search).get("edit_set");

    if (!editSet) {
      clear();
      return;
    }

    try {
      const known = setIdsOf(await fetchCart());

      // The sofa is no longer in the cart (removed in another tab, cart expired…): this is a plain add.
      if (!known.includes(editSet)) {
        clear();
        return;
      }

      write({ old: editSet, known, ts: Date.now() });
      showEditNotice();
    } catch (error) {
      clear();
    }
  }

  function showEditNotice() {
    const message = script && script.dataset.editNotice;
    if (!message) return;

    const notice = document.createElement("p");
    notice.className = "kc-sofa-edit-notice";
    notice.setAttribute("role", "status");
    notice.textContent = message;
    document.body.appendChild(notice);

    window.setTimeout(() => notice.remove(), 9000);
  }

  /* ---------------------------------------------------------------- *
   * Cart page
   * ---------------------------------------------------------------- */

  function status(messageKey, state) {
    const host = document.querySelector("[data-cart-edit-status]");
    if (!host) return;

    const message = messageKey ? host.dataset[messageKey] : "";
    host.textContent = message || "";
    host.hidden = !message;
    if (state) host.dataset.state = state;
    else delete host.dataset.state;
  }

  function unveil() {
    document.documentElement.classList.remove(RECONCILING_CLASS);
  }

  async function onCartPage() {
    // Second load, right after a successful replace: confirm it, once.
    try {
      if (sessionStorage.getItem(DONE_KEY)) {
        sessionStorage.removeItem(DONE_KEY);
        status("updatedMessage");
        window.setTimeout(() => status(null), 6000);
      }
    } catch (error) {
      /* no confirmation, the cart itself is already right */
    }

    const record = read();
    if (!record) {
      unveil();
      return;
    }

    // Whatever happens below, never leave the cart veiled.
    const safety = window.setTimeout(unveil, 8000);
    status("updatingMessage");

    try {
      const cart = await fetchCart();
      const current = setIdsOf(cart);
      const added = current.filter((id) => !record.known.includes(id));

      // Nothing new (came back without saving) or the old sofa is already gone: no edit to apply.
      if (added.length === 0 || !current.includes(record.old)) {
        clear();
        status(null);
        window.clearTimeout(safety);
        unveil();
        return;
      }

      const updates = {};
      cart.items.forEach((item) => {
        const properties = item.properties || {};
        if (
          properties._set_id === record.old ||
          properties._set === record.old ||
          properties._insured_set_id === record.old
        ) {
          updates[item.key] = 0;
        }
      });

      const response = await fetch(`${root()}cart/update.js`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ updates }),
      });
      if (!response.ok) throw new Error(`cart/update.js ${response.status}`);

      clear();
      try {
        localStorage.removeItem(record.old); // the old 3D preview
        sessionStorage.setItem(DONE_KEY, "1");
      } catch (error) {
        /* cosmetic only */
      }

      // Stay veiled through the reload, or the doubled order would flash for a frame.
      window.location.reload();
      return;
    } catch (error) {
      // The session is kept on purpose: the next visit to /cart retries. Both sofas stay visible meanwhile,
      // with a message, rather than silently showing a doubled order.
      console.error("[kc-sofa-edit] could not replace the edited sofa", error);
      status("failedMessage", "error");
    }

    window.clearTimeout(safety);
    unveil();
  }

  const run = () => {
    if (document.getElementById("productConfiguratorIFrame")) {
      onConfiguratorPage();
    } else if (document.querySelector("main-cart")) {
      onCartPage();
    }
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", run, { once: true });
  } else {
    run();
  }
})();
